import { fetch } from './helpers.ts';
import endpoints from '../endpoints.ts';
import { AddressType, ContactType, TradingPartnerType } from '../enums.ts';
import {
  Contact,
  Toast,
  TradingPartner,
  TradingPartnerAttachment,
} from '../types/models';
import { HydratedTradingPartner } from '../types/compoundModels.ts';
import { AxiosError } from 'axios';
import { logout, toast } from '../../func.js';

const tradingPartner = {
  create: {
    one: async (data: Partial<TradingPartner>): Promise<Toast | null> => {
      const url = endpoints.tradingPartner.create.one();

      const errorHandler = (err: AxiosError) => {
        const data = err.response?.data;
        const {
          payload: { msg },
        } = data as Toast;

        if (msg.includes('__unique_name_customer_location'))
          return toast({
            payload: {
              msg: 'Error: Duplicate Trading Partner Name.',
              class: 'red white-text',
            },
          });

        logout(err);
      };

      return await fetch<Toast>(url, 'POST', data, { errorHandler });
    },
    tradingPartnerGlobalLocationNumber: async (data: {
      tradingPartnerId: number;
      globalLocationNumber: string;
    }): Promise<Toast | null> => {
      const url =
        endpoints.tradingPartner.create.tradingPartnerGlobalLocationNumber();

      const errorHandler = (err: AxiosError) => {
        const data = err.response?.data;
        const {
          payload: { msg },
        } = data as Toast;

        if (msg.includes('__unique_partner_id_gln'))
          return toast({
            payload: {
              msg: 'This GLN is already in use by one of your trading partners.',
              class: 'red white-text',
            },
          });

        logout(err);
      };

      return await fetch<Toast>(url, 'POST', data, { errorHandler });
    },
    tradingPartnerAddress: async (data: {
      tradingPartnerId: number;
      stateId: number | null;
      addressType: AddressType;
      name: string;
      addressLineOne: string | null;
      addressLineTwo: string | null;
      city: string | null;
      postalCode: string | null;
    }): Promise<Toast | null> => {
      const url = endpoints.tradingPartner.create.tradingPartnerAddress();
      return await fetch<Toast>(url, 'POST', data);
    },
    tradingPartnerContact: async (data: {
      tradingPartnerId: number;
      firstName: string;
      lastName: string | null;
      title: string | null;
      contactDetails:
        | {
            contactType: ContactType;
            contactDetail: string;
          }[]
        | null;
    }): Promise<Toast | null> => {
      const url = endpoints.tradingPartner.create.tradingPartnerContact();
      return await fetch<Toast>(url, 'POST', data);
    },
    tradingPartnerContactDetail: async (data: {
      tradingPartnerContactId: number;
      contactType: ContactType;
      contactDetail: string;
    }): Promise<Toast | null> => {
      const url = endpoints.tradingPartner.create.tradingPartnerContactDetail();
      return await fetch<Toast>(url, 'POST', data);
    },
    tradingPartnerAccountNumber: async (data: {
      tradingPartnerId: number;
      accountNumber: string;
    }): Promise<Toast | null> => {
      const url = endpoints.tradingPartner.create.tradingPartnerAccountNumber();
      return await fetch<Toast>(url, 'POST', data);
    },
  },
  read: {
    one: {
      tradingPartner: {
        by: {
          id: async (id: number): Promise<TradingPartner | null> => {
            const url =
              endpoints.tradingPartner.read.one.tradingPartner.by.id();
            return await fetch<TradingPartner>(url, 'GET', { id });
          },
        },
      },
    },
    many: {
      tradingPartners: {
        search: async (data: {
          name: string;
          gln: string;
          stateId: string | number;
          type: TradingPartnerType;
        }): Promise<Partial<TradingPartner>[] | null> => {
          const url =
            endpoints.tradingPartner.read.many.tradingPartners.search();
          return await fetch<Partial<TradingPartner>[]>(url, 'GET', data);
        },
      },
      tradingPartnerContacts: {
        by: {
          outboundDocumentId: async (outboundDocumentId: number | string) => {
            const url =
              endpoints.tradingPartner.read.many.tradingPartnerContacts.by.outboundDocumentId();
            return await fetch<Contact[]>(url, 'GET', { outboundDocumentId });
          },
        },
      },
    },
    all: {
      attachments: {
        by: {
          tradingPartnerId: async (tradingPartnerId: number) => {
            const url =
              endpoints.tradingPartner.read.all.attachments.by.tradingPartnerId();
            return await fetch<TradingPartnerAttachment[]>(url, 'GET', {
              tradingPartnerId,
            });
          },
        },
      },
      by: {
        userId: async (): Promise<HydratedTradingPartner[] | null> => {
          const url = endpoints.tradingPartner.read.all.by.userId();
          return await fetch<HydratedTradingPartner[]>(url, 'GET');
        },
      },
    },
  },
  update: {
    one: {
      tradingPartner: async (data: {
        id: number | string;
        name: string;
        type: TradingPartnerType;
        isActive: boolean;
      }): Promise<Toast | null> => {
        const url = endpoints.tradingPartner.update.one.tradingPartner();
        return await fetch<Toast>(url, 'PUT', data);
      },
      tradingPartnerGlobalLocationNumber: async (data: {
        id: number | string;
        globalLocationNumber: string;
      }): Promise<Toast | null> => {
        const url =
          endpoints.tradingPartner.update.one.tradingPartnerGlobalLocationNumber();
        return await fetch<Toast>(url, 'PUT', data);
      },
      tradingPartnerAddress: async (data: {
        id: number | string;
        stateId: number | null;
        addressType: AddressType;
        name: string;
        addressLineOne: string | null;
        addressLineTwo: string | null;
        city: string | null;
        postalCode: string | null;
      }): Promise<Toast | null> => {
        const url = endpoints.tradingPartner.update.one.tradingPartnerAddress();
        return await fetch<Toast>(url, 'PUT', data);
      },
      tradingPartnerContact: async (data: {
        id: number;
        firstName: string;
        lastName: string | null;
        title: string | null;
      }) => {
        const url = endpoints.tradingPartner.update.one.tradingPartnerContact();
        return await fetch<Toast>(url, 'PUT', data);
      },
      tradingPartnerAccountNumber: async (data: {
        id: number;
        accountNumber: string;
      }): Promise<Toast | null> => {
        const url =
          endpoints.tradingPartner.update.one.tradingPartnerAccountNumber();
        return await fetch<Toast>(url, 'PUT', data);
      },
    },
  },
  delete: {
    one: {
      attachment: {
        by: {
          id: async (
            tradingPartnerAttachmentId: string,
            signal?: AbortSignal
          ): Promise<Toast | null> => {
            const url = endpoints.tradingPartner.delete.one.attachment.by.id();
            return await fetch<Toast | null>(
              url,
              'DELETE',
              { tradingPartnerAttachmentId },
              { signal }
            );
          },
        },
      },
      tradingPartnerGlobalLocationNumber: async (
        id: number
      ): Promise<Toast | null> => {
        const url =
          endpoints.tradingPartner.delete.one.tradingPartnerGlobalLocationNumber();
        return await fetch<Toast>(url, 'DELETE', { id });
      },
      tradingPartnerAddress: async (id: number): Promise<Toast | null> => {
        const url = endpoints.tradingPartner.delete.one.tradingPartnerAddress();
        return await fetch<Toast>(url, 'DELETE', { id });
      },
      tradingPartnerContact: async (id: number): Promise<Toast | null> => {
        const url = endpoints.tradingPartner.delete.one.tradingPartnerContact();
        return await fetch<Toast>(url, 'DELETE', { id });
      },
      tradingPartnerContactDetail: async (
        id: number
      ): Promise<Toast | null> => {
        const url =
          endpoints.tradingPartner.delete.one.tradingPartnerContactDetail();
        return await fetch<Toast>(url, 'DELETE', { id });
      },
      tradingPartnerAccountNumber: async (
        id: number
      ): Promise<Toast | null> => {
        const url =
          endpoints.tradingPartner.delete.one.tradingPartnerAccountNumber();
        return await fetch<Toast>(url, 'DELETE', { id });
      },
    },
  },
  download: {
    one: {
      attachment: {
        by: {
          id: async (
            id: number,
            signal: AbortSignal
          ): Promise<Buffer | null> => {
            const url =
              endpoints.tradingPartner.download.one.attachment.by.id();
            return await fetch<Buffer | null>(
              url,
              'GET',
              { tradingPartnerAttachmentId: id },
              {
                signal,
                responseType: 'arraybuffer',
              }
            );
          },
        },
      },
    },
  },
  upload: {
    many: {
      attachments: async (
        formData: FormData,
        signal?: AbortSignal
      ): Promise<string | null> => {
        const url = endpoints.tradingPartner.upload.many.attachments();
        return await fetch<string | null>(url, 'POST', formData, {
          signal,
        });
      },
    },
    one: {
      attachment: async (
        formData: FormData,
        signal?: AbortSignal
      ): Promise<string | null> => {
        const url = endpoints.tradingPartner.upload.one.attachment();
        return await fetch<string | null>(url, 'POST', formData, {
          signal,
        });
      },
    },
  },
};

export default tradingPartner;
